import {
  fetchMyInvoices,
  fetchMyWatchedLotIds,
  fetchPickupSales,
  houseLabel,
  invoiceIsOutstanding,
  invoiceStatusLabel,
  totalIsAmountOwed,
  type MyInvoice,
  type PickupSale,
} from '@auctisync/api-client';
import { formatCents } from '@auctisync/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

/**
 * Overview — the account's front page.
 *
 * NEW (web design handoff, `web/web-account.jsx`, 2026-09-02). `/my/dashboard`
 * used to be the FULL bidding list; there was no summary anywhere, so a bidder
 * signing in to answer "am I still winning, and do I owe anything?" had to read
 * two long pages to find out.
 *
 * 🔴 FOUR NUMBERS, AND EACH ONE IS A LINK. A stat that cannot be acted on is
 * decoration; every figure here goes to the section that explains it.
 *
 * "OWED" COUNTS ONLY OUTSTANDING INVOICES, through `invoiceIsOutstanding` and
 * `totalIsAmountOwed` from the shared client. A paid invoice's total is not a
 * debt, and summing every invoice ever raised would tell a bidder they owe
 * thousands the moment they have bought anything.
 */
export function AccountOverview() {
  const { session, userId } = useSession();
  const { active } = useBidderHouses();

  const [invoices, setInvoices] = useState<MyInvoice[]>([]);
  const [watching, setWatching] = useState(0);
  const [standing, setStanding] = useState({ winning: 0, outbid: 0 });
  const [pickup, setPickup] = useState<PickupSale[]>([]);
  const [loading, setLoading] = useState(true);

  const tenantId = active?.tenant_id ?? null;
  const houseName = active === null ? 'the auction house' : houseLabel(active);

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      // Winning / outbid from the database's own counter — the rule ("open
      // lots only", "outbid means you bid and no longer lead") lives in SQL,
      // so this card, My bids and the live room cannot disagree. It used to
      // read `lots.current_bidder_user_id`, which a signed-in bidder may not
      // read (2026-09-23): the read failed and both numbers sat at zero.
      const [counts, watched, invoiceRows] = await Promise.all([
        supabase.rpc('get_my_standing_counts', {}),
        fetchMyWatchedLotIds(supabase, userId).catch((): string[] => []),
        fetchMyInvoices(supabase, userId),
      ]);
      if (cancelled) return;

      const row = Array.isArray(counts.data) ? counts.data[0] : undefined;
      setStanding({ winning: row?.winning ?? 0, outbid: row?.outbid ?? 0 });
      setWatching(watched.length);
      setInvoices(invoiceRows);
      setLoading(false);

      // Pickup is a second wave: it costs one read per outstanding invoice, and
      // the numbers above should not wait behind it.
      const sales = await fetchPickupSales(supabase, { userId, tenantId });
      if (!cancelled) setPickup(sales);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, tenantId]);

  const outstanding = invoices.filter((i) => invoiceIsOutstanding(i.status));
  const owedCents = outstanding.reduce(
    (sum, i) => sum + (totalIsAmountOwed(i.status) ? i.total_cents : 0),
    0,
  );
  const nextPickup = pickup[0] ?? null;

  return (
    <>
      <div className="wa-hd">
        <div>
          <h1 className="w-h1" style={{ fontSize: 32 }}>
            Your account
          </h1>
          <p className="muted" style={{ marginTop: 6, fontSize: 14 }}>
            {session?.user.email ?? ''}
          </p>
        </div>
      </div>

      <div className="wa-stat">
        <Link to="/my/bidding" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="w-lbl">Winning</span>
          <b style={{ color: 'var(--semantic-winning)' }}>{standing.winning}</b>
        </Link>
        <Link to="/my/bidding" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="w-lbl">Outbid</span>
          <b style={{ color: standing.outbid > 0 ? 'var(--semantic-outbid)' : undefined }}>
            {standing.outbid}
          </b>
        </Link>
        <Link to="/my/bidding" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="w-lbl">Watching</span>
          <b>{watching}</b>
        </Link>
        <Link to="/my/invoices" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="w-lbl">Owed</span>
          <b>{formatCents(owedCents)}</b>
        </Link>
      </div>

      {loading ? <p className="muted">Loading…</p> : null}

      {outstanding.length > 0 ? (
        <section style={{ marginBottom: 26 }}>
          <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 14 }}>
            To pay
          </h2>
          <div className="wa-card">
            {outstanding.map((inv) => (
              <Link key={inv.id} to={`/my/invoices/${inv.id}`} className="wa-row">
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ display: 'block', fontWeight: 600 }}>
                    {inv.auction?.title ?? 'Auction'}
                  </b>
                  <span className="muted" style={{ fontSize: 13 }}>
                    {inv.invoice_number ?? 'Invoice'} · {invoiceStatusLabel(inv.status)}
                  </span>
                </span>
                <b className="mono">{formatCents(inv.total_cents)}</b>
                <span aria-hidden className="muted">
                  ›
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {nextPickup !== null ? (
        <section style={{ marginBottom: 26 }}>
          <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 14 }}>
            To collect
          </h2>
          {/*
            THE INVERTED BRAND BAND, not red. Pickup is not an alarm, and red is
            locked to the auction semantics — closing, and outbid. A red plate
            here would teach a bidder to read "collection" as "something has gone
            wrong". Same decision as the phone's pickup screen.
          */}
          <div className="wa-band">
            <div className="w-lbl">Next window · {houseName}</div>
            <div
              className="w-h2"
              style={{ marginTop: 6, color: 'var(--text-on-deep)', fontSize: 22 }}
            >
              {nextPickup.pickup.window ?? 'Collection times not set yet'}
            </div>
            {nextPickup.pickup.address !== null ? (
              <p style={{ margin: '6px 0 0', color: 'var(--text-label-on-deep)', fontSize: 14 }}>
                {nextPickup.pickup.address}
              </p>
            ) : null}
            <Link to="/my/pickup" className="w-btn w-btn-ghost w-btn-sm" style={{ marginTop: 16 }}>
              Pickup details<span aria-hidden>›</span>
            </Link>
          </div>
        </section>
      ) : null}

      {!loading && outstanding.length === 0 && standing.winning === 0 && standing.outbid === 0 ? (
        <div className="wa-card" style={{ padding: 20 }}>
          <b style={{ fontFamily: 'var(--font-heading)', fontSize: 15 }}>
            Nothing needs you right now
          </b>
          <p className="muted" style={{ margin: '6px 0 14px', fontSize: 14, lineHeight: 1.55 }}>
            No open bids, nothing owed and nothing waiting for collection.
          </p>
          <Link to="/browse" className="w-btn w-btn-cta w-btn-sm">
            Browse the lots
          </Link>
        </div>
      ) : null}
    </>
  );
}
